import ModificationType from './reference/ModificationType';
import TransactionType from './reference/TransactionType';
import ComponentType from './reference/ComponentType';
import { prepareScheduleBySalesOrderItemId, adjustForBilling } from './BillingScheduleHepler';
import { Utils, Comparator, Period } from 'revlock-webutils';
import shortid from "shortid";
import _ from 'lodash'

const endOfTransactions = {
    end: true
}

export const CONFIG = {
    revenue: {
        componentType: ComponentType.UNEARNED_REVENUE,
        originalTransactionType: TransactionType.PERFORMANCE_OBLIGATION_ORIGIN,
        ammortizationTransactionType: TransactionType.REVENUE,
        getPlan: item => item.plan || [],
        getTotalBooking: item => item.terminationDate ? item.terminatedProductRevenue : item.productRevenue,
        getRecognitionRule: item => item.recognitionRule,
        getEndDate: item => item.deliveryEndDate,
        getJournalTransactions: O => O.reportingTransactions,
        addJournalEntries: (O, journalEntries) => O.journalEntries = journalEntries,
        createEntries: (reportingTransaction, salesOrder, salesOrderItemId, config, defaultAccounts, jeMappingColumns) => {

            let transactions = []

            if (reportingTransaction['Foreign Currency Change'] != 0) {
                transactions.push({
                    'Component Type': ComponentType.UNEARNED_REVENUE,
                    'Transaction Type': TransactionType.REVENUE,
                    'Amount': reportingTransaction['Foreign Currency Change'],
                    'Link Id': `RevRec_${reportingTransaction['Actg Period']}`,
                    'SOI Version Item Id': salesOrderItemId
                })
            }

            if (reportingTransaction['Revenue'] != 0) {
                transactions.push({
                    'Component Type': ComponentType.UNEARNED_REVENUE,
                    'Transaction Type': TransactionType.REVENUE,
                    'Amount': reportingTransaction['Revenue'],
                    'Link Id': `RevRec_${reportingTransaction['Actg Period']}`,
                    'SOI Version Item Id': salesOrderItemId
                })
            }

            if (reportingTransaction['New Billing'] != 0) {
                transactions.push({
                    'Component Type': ComponentType.UNEARNED_REVENUE,
                    'Transaction Type': TransactionType.PERFORMANCE_OBLIGATION_ORIGIN,
                    'Amount': -1 * reportingTransaction['New Billing'],
                    'Link Id': `RevDef_${reportingTransaction['Actg Period']}`,
                    'SOI Version Item Id': salesOrderItemId
                })
            }

            // Build base journal mapping value.
            const baseJournalMappingValues = [];

            for (const jeMappingColumn of jeMappingColumns) {
                baseJournalMappingValues.push(
                    reportingTransaction[jeMappingColumn] || "JEMINVOICE");
            }

            const baseJournalMappingValue = baseJournalMappingValues.join(':=:');

            let journalEntries = []
            transactions.forEach(transaction => {
                let amount = transaction['Amount'];

                delete transaction['Amount']

                if (amount == 0) {
                    return
                }

                let inverse = amount < 0;
                amount = Math.abs(amount);

                let debitAccount = defaultAccounts["Deferred Revenue"] || { accountType: 'Deferred Revenue' }
                let creditAccount = defaultAccounts["Revenue"] || { accountType: 'Revenue' }

                // REV_DEFERRAL Transaction is when there is New_Billing <> 0
                if (transaction['Transaction Type'] === config.originalTransactionType) {
                    // Taking out amount from Contra revenue instead of Revenue Account here.
                    creditAccount = Utils.cloneDeep(creditAccount)
                    creditAccount.accountType = 'Contra Revenue'
                }

                if (inverse) {
                    [debitAccount, creditAccount] = [creditAccount, debitAccount]
                }

                reportingTransaction['Transaction Date'] = reportingTransaction['Transaction Date']

                let baseEntry = Object.assign({}, _.pick(reportingTransaction, [
                    'OrgId',
                    'Actg Period',
                    'Transaction Date',
                    'Sales Order Item Id',
                    'Sales Order Item Root Id',
                    'Customer Id',
                    'Sales Order Id',
                    'Linked Sales Order Id',
                    'Product Id',
                    'Job Id',
                    'IsActive',
                    'Active Link Id',
                    'Currency',
                    'Home Currency',
                    'IsAdjustment'
                ]), transaction)

                baseEntry['Sales Order Item Id'] = reportingTransaction['Sales Order Item Root Id']

                // These are for expense only but if we don't set them as follows
                // then they end up in JE table as empty strings instead of NULL
                baseEntry['Reference Number'] = '\\N';
                baseEntry['Expense Type'] = '\\N';
                baseEntry['Expense Code'] = '\\N';
                baseEntry['Class'] = '\\N';
                baseEntry['Department'] = '\\N';
                baseEntry['Location'] = '\\N';
                baseEntry['Sales Person'] = '\\N';

                // Ensures that the AdjustmentType is NULL and not empty string
                // if its not specified.
                const isAdjustment = reportingTransaction['IsAdjustment'];
                baseEntry['AdjustmentType'] =
                    isAdjustment == 1 ? reportingTransaction['AdjustmentType'] : '\\N';

                let debitEntry = Object.assign({
                    "Account Id": debitAccount && debitAccount.id,
                    "Account Type": debitAccount && debitAccount.accountType,
                    "Debit Amount": amount,
                    "Credit Amount": 0,
                    "Net Amount": amount
                }, baseEntry, {
                    "Journal Mapping Key":
                        baseJournalMappingValue.length > 0
                            ? baseJournalMappingValue + ":=:" + debitAccount.accountType
                            : debitAccount.accountType
                })

                let creditEntry = Object.assign({
                    "Account Id": creditAccount && creditAccount.id,
                    "Account Type": creditAccount && creditAccount.accountType,
                    "Debit Amount": 0,
                    "Credit Amount": -1 * amount,
                    "Net Amount": -1 * amount
                }, baseEntry, {
                    "Journal Mapping Key":
                        baseJournalMappingValue.length > 0
                            ? baseJournalMappingValue + ":=:" + creditAccount.accountType
                            : creditAccount.accountType
                })

                journalEntries.push(debitEntry, creditEntry)
            })

            return journalEntries
        }
    },
    expense: {
        componentType: ComponentType.EXPENSE,
        originalTransactionType: TransactionType.EXP_DEFERRAL,
        ammortizationTransactionType: TransactionType.EXP_RECOGNIZED,
        getPlan: item => item.plan || [],
        getTotalBooking: item => item.allocatedExpense,
        getRecognitionRule: item => item.expenseAmortizationMethod,
        getEndDate: item => item.deliveryEndDate,
        getJournalTransactions: O => O.expenseTransactions,
        addJournalEntries: (O, journalEntries) => O.expenseJournalEntries = journalEntries,
        createEntries: (reportingTransaction, salesOrder, salesOrderItemId,
            config, defaultAccounts, jeMappingColumns) => {
            let transactions = []

            if (reportingTransaction['Revenue'] != 0) {
                transactions.push({
                    'Component Type': ComponentType.EXPENSE,
                    'Transaction Type': TransactionType.EXP_RECOGNIZED,
                    'Amount': reportingTransaction['Revenue'],
                    'Link Id': `ExpAmort_${reportingTransaction['Actg Period']}`,
                    'SOI Version Item Id': salesOrderItemId
                })
            }

            if (reportingTransaction['New Billing'] != 0) {
                transactions.push({
                    'Component Type': ComponentType.EXPENSE,
                    'Transaction Type': TransactionType.EXP_DEFERRAL,
                    'Amount': -1 * reportingTransaction['New Billing'],
                    'Link Id': `ExpDef_${reportingTransaction['Actg Period']}`,
                    'SOI Version Item Id': salesOrderItemId
                })
            }

            // Build base journal mapping value.
            const baseJournalMappingValues = [];
            
            for (const jeMappingColumn of jeMappingColumns) {
                baseJournalMappingValues.push(
                    reportingTransaction[jeMappingColumn] || "JEMINVOICE");
            }

            const baseJournalMappingValue = baseJournalMappingValues.join(':=:');
            
            let journalEntries = []
            transactions.forEach(transaction => {
                let transactionDate = Period.toEndOfMonth(reportingTransaction["Actg Period"]);
                let amount = transaction['Amount'];

                delete transaction['Amount']

                if (amount == 0) {
                    return
                }

                let inverse = amount < 0;
                amount = Math.abs(amount);

                let debitAccount,
                    creditAccount

                if (transaction['Transaction Type'] === config.originalTransactionType) {
                    debitAccount = defaultAccounts['Expense Clearing'] || { accountType: 'Expense Clearing' };
                    creditAccount = defaultAccounts['Deferred Expense'] || { accountType: 'Deferred Expense' }
                } else {
                    debitAccount = defaultAccounts['Expense Recognized'] || { accountType: 'Expense Recognized' }
                    creditAccount = defaultAccounts['Commission Accumulated Amortization'] || defaultAccounts['Deferred Expense'] || { accountType: 'Deferred Expense' }
                }

                if (inverse) {
                    [debitAccount, creditAccount] = [
                        creditAccount, debitAccount]
                    // credit will be contra
                    // debit will be deferred
                }

                let baseEntry = Object.assign({
                    'OrgId': reportingTransaction['OrgId'],
                    "Actg Period": reportingTransaction['Actg Period'],
                    "Sales Order Id": reportingTransaction['Sales Order Id'],
                    'Linked Sales Order Id': reportingTransaction['Linked Sales Order Id'],
                    "Sales Order Item Id": reportingTransaction['Sales Order Item Root Id'],
                    "Sales Order Item Root Id": reportingTransaction['Sales Order Item Root Id'],
                    "Product Id": reportingTransaction["Product Id"],
                    "Transaction Date": transactionDate,
                    "Customer Id": reportingTransaction["Customer Id"],
                    "Component Type": reportingTransaction["componentType"],
                    "Job Id": reportingTransaction["Job Id"],
                    "IsActive": reportingTransaction["Is Active"],
                    "Reference Number": reportingTransaction["Reference Number"],
                    "Expense Type": reportingTransaction["Expense Type"] || '\\N',
                    "Expense Code": reportingTransaction["Expense Code"] || '\\N',
                    "Class": reportingTransaction["Class"] || '\\N',
                    "Department": reportingTransaction["Department"] || '\\N',
                    "Location": reportingTransaction["Location"] || '\\N',
                    "Sales Person": reportingTransaction["Sales Person"] || '\\N',
                    "Active Link Id": reportingTransaction['Active Link Id'],
                    'Currency': reportingTransaction['Currency'],
                    'Home Currency': reportingTransaction['Home Currency'],
                    'IsAdjustment': reportingTransaction['IsAdjustment'],
                    'AdjustmentType': reportingTransaction['IsAdjustment'] == 1
                                            ? reportingTransaction['AdjustmentType']
                                            : '\\N'
                }, transaction);

                let debitEntry = Object.assign({
                    "Account Id": debitAccount && debitAccount.id,
                    "Account Type": debitAccount && debitAccount.accountType,
                    "Debit Amount": amount,
                    "Credit Amount": 0,
                    "Net Amount": amount
                }, baseEntry, {
                    "Journal Mapping Key":
                        baseJournalMappingValue.length > 0
                            ? baseJournalMappingValue + ":=:" + debitAccount.accountType
                            : debitAccount.accountType
                })

                let creditEntry = Object.assign({
                    "Account Id": creditAccount && creditAccount.id,
                    "Account Type": creditAccount && creditAccount.accountType,
                    "Debit Amount": 0,
                    "Credit Amount": amount == 0 ? 0 : -1 * amount,
                    "Net Amount": amount == 0 ? 0 : -1 * amount,
                }, baseEntry, {
                    "Journal Mapping Key":
                        baseJournalMappingValue.length > 0
                            ? baseJournalMappingValue + ":=:" + creditAccount.accountType
                            : creditAccount.accountType
                })

                journalEntries.push(debitEntry, creditEntry)
            });

            return journalEntries
        }
    }
}

export function generateExpenseTransactions(salesOrder, calendarConfig) {
    let config = CONFIG['expense'];

    let { getEndDate } = config;

    if (!salesOrder.currentRevenueArrangement)
        return;

    // Order arrangements by effective date.
    let comparator = (ra1, ra2) => ra2.effectiveDate < ra1.effectiveDate ? 1 : -1
    salesOrder.revenueArrangement.sort(comparator);


    // Collect transactions in following array.
    let transactions = []
    let needOriginalTransactions = true;
    let firstPeriod = '210001';
    let lastPeriod = '200001'
    let pComp = Comparator.forType('period');

    const lastestVersionItem = {}
    salesOrder.currentRevenueArrangement.revenueArrangementItem.forEach(rai => {
        rai.expenseItems.forEach(item => {
            let _itemId = item.salesOrderItemRootId || item.salesOrderItemId
            lastestVersionItem[_itemId] = rai.salesOrderItem.id
        })
    })

    // For each arrangement,
    salesOrder.revenueArrangement.forEach(
        revenueArrangement => {

            revenueArrangement.revenueArrangementItem.forEach(rai => {
                rai.expenseItems.forEach(item => {
                    let _itemId = item.salesOrderItemRootId || item.salesOrderItemId
                    if (!lastestVersionItem[_itemId])
                        lastestVersionItem[_itemId] = rai.salesOrderItem.id
                })
            })

            if (revenueArrangement.state == 'New') {
                return;
            }

            let { endDate, modificationType } = revenueArrangement
            const arrangementFirstActgPeriod = Period.toActgPeriod(revenueArrangement.effectiveDate, calendarConfig)

            // If end date is null i.e. arrangement is active
            if (!endDate) {
                // Set end date to the   deliveryEndDate of the revenueArrangementItem with longest duration
                endDate = revenueArrangement.revenueArrangementItem[0].deliveryEndDate
                revenueArrangement.revenueArrangementItem.forEach(rai => {
                    rai.expenseItems.forEach(item => {
                        let _endDate = getEndDate(item)
                        if (_endDate > endDate) {
                            endDate = _endDate
                        }
                    })
                })
            }

            const endPeriod = Period.toActgPeriod(endDate, calendarConfig);
            endDate = endPeriod.endDate;

            // Current effective date is arrangement's effective date, this might change if arrangement has retrospective mod.
            const currentActgPeriod = Period.toActgPeriod(Period.currentDate(), calendarConfig)
            const currentPostingPeriod = currentActgPeriod.period
            const currentPostingPeriodDate = currentActgPeriod.endDate

            // If retrospective MOD
            if (modificationType && modificationType === ModificationType.RETROSPECTIVE) {
                // Create reversal transaction for all existing enteries
                let reversals = transactions.reduce(
                    (result, transaction) => {
                        // Only reverse where transaction type is REVENUE.
                        if (!transaction.skipReversal) {
                            const actgPeriod = arrangementFirstActgPeriod
                            const isFutureTransaction =
                                Period.isSameOrAfter(actgPeriod.startDate, currentPostingPeriodDate);

                            const reversal = {
                                id: transaction.id + "_reversal",
                                actgPeriod,
                                revenueArrangementItemId: transaction.revenueArrangementItemId,
                                expenseArrangementItemId: transaction.expenseArrangementItemId,
                                salesOrderItemId: lastestVersionItem[transaction.salesOrderItemRootId],
                                salesOrderItemRootId: transaction.salesOrderItemRootId,
                                salesOrderExpenseItemId: transaction.salesOrderExpenseItemId,
                                effectiveDate: actgPeriod.endDate,
                                postingDate: actgPeriod.postingDate,
                                period: transaction.period,
                                postingPeriod: actgPeriod.period,
                                postingYear: actgPeriod.strYear,
                                amount: -1 * transaction.amount,
                                transactionType: transaction.transactionType,
                                componentType: transaction.componentType,
                                isFutureTransaction: isFutureTransaction,
                                totalBooking: transaction.totalBooking,
                                processType: 'REVERSAL',
                                productId: transaction.productId,
                                expenseProductCode: transaction.expenseProductCode,
                                expenseCode: transaction.expenseCode,
                                expenseType: transaction.expenseType,
                                referenceNumber: transaction.referenceNumber,
                                class: transaction.class,
                                department: transaction.department,
                                location: transaction.location,
                                customerId: transaction.customerId,
                                salesOrderId: salesOrder.id,
                                isActive: transaction.isActive,
                                isModification: true,
                                salesPerson: transaction.salesPerson
                            }

                            // No need to reverse again in case there are more mods.
                            transaction.skipReversal = true;
                            reversal.skipReversal = true;

                            /*
                            reversal.cummulativePercentRecognized += (reversal.amount / reversal.totalBooking)
                            */
                            result.push(reversal);
                        }
                        return result;
                    }, []
                )

                transactions.push(...reversals);

                needOriginalTransactions = true;
            }

            if (needOriginalTransactions) {
                let originActgPeriod = Period.toActgPeriod(revenueArrangement.effectiveDate, calendarConfig);

                let isMod = revenueArrangement.state == 'Closed'
                revenueArrangement.revenueArrangementItem.forEach(rai => {
                    let { salesOrderItem } = rai;
                    rai.expenseItems.forEach(item => {

                        const _originActgPeriod = Period.toActgPeriod(item.expenseDate, calendarConfig)

                        if (_originActgPeriod.period > originActgPeriod.period) {
                            // Expense Date is after current revenue arrangement we are
                            // iterating over
                            originActgPeriod = _originActgPeriod
                        }

                        const isFutureTransaction = Period.isSameOrAfter(originActgPeriod.endDate, currentPostingPeriodDate);

                        if (originActgPeriod.period < firstPeriod)
                            firstPeriod = originActgPeriod.period;
                        if (originActgPeriod.period > lastPeriod)
                            lastPeriod = originActgPeriod.period;

                        let totalBooking = config.getTotalBooking(item)
                        let transaction = {
                            id: shortid.generate(),
                            actgPeriod: originActgPeriod,
                            revenueArrangementItemId: rai.id,
                            expenseArrangementItemId: item.id,
                            salesOrderItemId: lastestVersionItem[item.salesOrderItemRootId || item.salesOrderItemId],
                            salesOrderItemRootId: item.salesOrderItemRootId || item.salesOrderItemId,
                            salesOrderExpenseItemId: item.salesOrderExpenseItemId,
                            effectiveDate: originActgPeriod.endDate,
                            postingDate: originActgPeriod.postingDate,
                            period: originActgPeriod.period,
                            postingPeriod: originActgPeriod.period,
                            postingYear: originActgPeriod.strYear,
                            amount: -1 * totalBooking,
                            transactionType: config.originalTransactionType,
                            componentType: config.componentType,
                            isFutureTransaction: isFutureTransaction,
                            cummulativePercentRecognized: -1,
                            totalBooking: totalBooking,
                            processType: 'NORMAL',
                            productId: salesOrderItem.productId,
                            expenseProductCode: item.productCode,
                            expenseCode: item.expenseCode,
                            expenseType: item.expenseType,
                            referenceNumber: item.referenceNumber,
                            class: item.class,
                            department: item.department,
                            location: item.location,
                            customerId: salesOrder.customerId,
                            salesOrderId: salesOrder.id,
                            isActive: item.isActive,
                            isModification: isMod,
                            salesPerson: item.salesPerson?.name
                        };
                        transactions.push(transaction);
                    })
                })
                needOriginalTransactions = false;
            }

            revenueArrangement.revenueArrangementItem.forEach(rai => {
                let { salesOrderItem } = rai
                rai.expenseItems.forEach(item => {
                    let plan = config.getPlan(item);

                    let expenseEffectiveDateToCheckAgainstPlanEffectiveDate
                    if (Period.isAfter(item.expenseDate, revenueArrangement.effectiveDate)) {
                        // Expense Date is after current revenue arrangement we are
                        // iterating over
                        expenseEffectiveDateToCheckAgainstPlanEffectiveDate = item.expenseDate
                    } else {
                        expenseEffectiveDateToCheckAgainstPlanEffectiveDate = revenueArrangement.effectiveDate
                    }

                    // let's say a closed state revenueArrangement is type mod for now
                    let isMod = revenueArrangement.state == 'Closed'
                    let newTransactions = plan.reduce(
                        (result, planElement) => {
                            // Scan period from current effective date to end date
                            // we are comparing endDate (function) which would give us un-expected behavior
                            let _endDate = Utils.formatDate(endDate) // TODO: why do we need this??
                            if ((isMod & Period.isBefore(planElement.effectiveDate, _endDate))
                                || (!isMod && Period.isSameOrBefore(planElement.effectiveDate, _endDate))) {

                                let postingDate = planElement.postingDate;
                                let postingPeriod = planElement.postingPeriod;
                                let postingYear = Period.toYear(planElement.effectiveDate);

                                if (Period.isSameOrBefore(planElement.effectiveDate, expenseEffectiveDateToCheckAgainstPlanEffectiveDate)) {
                                    postingDate = arrangementFirstActgPeriod.postingDate
                                    postingPeriod = arrangementFirstActgPeriod.period;
                                    postingYear = arrangementFirstActgPeriod.strYear;
                                }

                                // Create plan transaction.
                                let t = {
                                    id: shortid.generate(),
                                    actgPeriod: planElement.actgPeriod,
                                    planId: planElement.id,
                                    revenueArrangementItemId: rai.id,
                                    expenseArrangementItemId: item.id,
                                    salesOrderItemId: lastestVersionItem[item.salesOrderItemRootId || item.salesOrderItemId],
                                    salesOrderItemRootId: item.salesOrderItemRootId || item.salesOrderItemId,
                                    salesOrderExpenseItemId: item.salesOrderExpenseItemId,
                                    effectiveDate: planElement.effectiveDate,
                                    postingDate: postingDate,
                                    period: planElement.period,
                                    postingPeriod: postingPeriod,
                                    postingYear: postingYear,
                                    amount: planElement.planAmount,
                                    transactionType: config.ammortizationTransactionType,
                                    componentType: config.componentType,
                                    isFutureTransaction: Period.isSameOrAfter(postingPeriod, currentPostingPeriod),
                                    cummulativePercentRecognized: planElement.cummulativePercentRecognized,
                                    totalBooking: config.getTotalBooking(item),
                                    processType: 'NORMAL',
                                    productId: salesOrderItem.productId,
                                    expenseProductCode: item.productCode,
                                    expenseCode: item.expenseCode,
                                    expenseType: item.expenseType,
                                    referenceNumber: item.referenceNumber,
                                    class: item.class,
                                    department: item.department,
                                    location: item.location,
                                    customerId: salesOrder.customerId,
                                    salesOrderId: salesOrder.id,
                                    jobId: salesOrder.jobId,
                                    isActive: item.isActive,
                                    isModification: isMod,
                                    salesPerson: item.salesPerson?.name
                                }

                                result.push(t);

                                if (pComp(postingPeriod, firstPeriod) < 0)
                                    firstPeriod = postingPeriod

                                if (pComp(postingPeriod, lastPeriod) > 0)
                                    lastPeriod = postingPeriod
                            }

                            return result
                        }, []
                    )

                    transactions.push(...newTransactions);
                })
            })
        }
    )

    salesOrder['expenseTransactions'] = transactions
    salesOrder['expenseFirstPeriod'] = firstPeriod;
    salesOrder['expenseLastPeriod'] = lastPeriod;
}

export function generateTransactions(salesOrder, calendarConfig) {
    let config = CONFIG['revenue'];

    let { getEndDate } = config;

    if (!salesOrder.currentRevenueArrangement)
        return;

    // Order arrangements by effective date.
    let comparator = (ra1, ra2) => ra2.effectiveDate < ra1.effectiveDate ? 1 : -1
    salesOrder.revenueArrangement.sort(comparator);


    // Collect transactions in following array.
    let transactions = []
    let needOriginalTransactions = true;
    let firstPeriod = '210001';
    let lastPeriod = '200001'
    let pComp = Comparator.forType('period');

    const lastestVersionItem = {}
    salesOrder.currentRevenueArrangement.revenueArrangementItem.forEach(rai => {
        let _itemId = rai.salesOrderItem.rootId || rai.salesOrderItem.id
        lastestVersionItem[_itemId] = rai.salesOrderItem.id
    })

    // For each arrangement,
    salesOrder.revenueArrangement.forEach(
        revenueArrangement => {

            revenueArrangement.revenueArrangementItem.forEach(rai => {
                let _itemId = rai.salesOrderItem.rootId || rai.salesOrderItem.id
                if (!lastestVersionItem[_itemId])
                    lastestVersionItem[_itemId] = rai.salesOrderItem.id
            })

            if (revenueArrangement.state == 'New') {
                return;
            }

            let { endDate, modificationType } = revenueArrangement
            const arrangementFirstActgPeriod = Period.toActgPeriod(revenueArrangement.effectiveDate, calendarConfig)

            // If end date is null i.e. arrangement is active
            if (!endDate) {
                // Set end date to the   deliveryEndDate of the revenueArrangementItem with longest duration
                endDate = revenueArrangement.revenueArrangementItem[0].deliveryEndDate
                revenueArrangement.revenueArrangementItem.forEach(item => {
                    let _endDate = getEndDate(item)
                    if (_endDate > endDate) {
                        endDate = _endDate
                    }
                })
            }

            const endPeriod = Period.toActgPeriod(endDate, calendarConfig);
            endDate = endPeriod.endDate;

            // Current effective date is arrangement's effective date, this might change if arrangement has retrospective mod.
            const currentActgPeriod = Period.toActgPeriod(Period.currentDate(), calendarConfig)
            const currentPostingPeriod = currentActgPeriod.period
            const currentPostingPeriodDate = currentActgPeriod.endDate

            // If retrospective MOD
            if (modificationType && modificationType === ModificationType.RETROSPECTIVE) {
                // Create reversal transaction for all existing enteries
                let reversals = transactions.reduce(
                    (result, transaction) => {
                        // Only reverse where transaction type is REVENUE.
                        if (!transaction.skipReversal) {
                            const actgPeriod = arrangementFirstActgPeriod
                            const isFutureTransaction =
                                Period.isSameOrAfter(actgPeriod.startDate, currentPostingPeriodDate);

                            const reversal = {
                                id: transaction.id + "_reversal",
                                actgPeriod,
                                revenueArrangementItemId: transaction.revenueArrangementItemId,
                                salesOrderItemId: lastestVersionItem[transaction.salesOrderItemRootId],
                                salesOrderItemRootId: transaction.salesOrderItemRootId,
                                effectiveDate: actgPeriod.endDate,
                                postingDate: actgPeriod.postingDate,
                                period: transaction.period,
                                postingPeriod: actgPeriod.period,
                                postingYear: actgPeriod.strYear,
                                amount: -1 * transaction.amount,
                                productRevenue: transaction.productRevenue,
                                extendedSalePrice: transaction.extendedSalePrice,
                                transactionType: transaction.transactionType,
                                componentType: transaction.componentType,
                                isFutureTransaction: isFutureTransaction,
                                totalBooking: transaction.totalBooking,
                                processType: 'REVERSAL',
                                productId: transaction.productId,
                                customerId: transaction.customerId,
                                salesOrderId: salesOrder.id,
                                isActive: transaction.isActive,
                                isModification: true
                            }

                            // No need to reverse again in case there are more mods.
                            transaction.skipReversal = true;
                            reversal.skipReversal = true;

                            /*
                            reversal.cummulativePercentRecognized += (reversal.amount / reversal.totalBooking)
                            */
                            result.push(reversal);
                        }
                        return result;
                    }, []
                )

                transactions.push(...reversals);

                needOriginalTransactions = true;
            }

            if (needOriginalTransactions) {
                const originActgPeriod = Period.toActgPeriod(revenueArrangement.effectiveDate, calendarConfig);

                if (originActgPeriod.period < firstPeriod)
                    firstPeriod = originActgPeriod.period;
                if (originActgPeriod.period > lastPeriod)
                    lastPeriod = originActgPeriod.period;


                revenueArrangement.revenueArrangementItem.forEach(item => {

                    let totalBooking = config.getTotalBooking(item)
                    let { salesOrderItem } = item;
                    let isMod = revenueArrangement.state == 'Closed'
                    // Unearned revenue transaction.
                    const isFutureTransaction = Period.isSameOrAfter(originActgPeriod.endDate, currentPostingPeriodDate);

                    let transaction = {
                        id: shortid.generate(),
                        actgPeriod: originActgPeriod,
                        revenueArrangementItemId: item.id,
                        salesOrderItemId: lastestVersionItem[item.salesOrderItem.rootId || item.salesOrderItemId],
                        salesOrderItemRootId: item.salesOrderItem.rootId || item.salesOrderItemId,
                        effectiveDate: originActgPeriod.endDate,
                        postingDate: originActgPeriod.postingDate,
                        period: originActgPeriod.period,
                        postingPeriod: originActgPeriod.period,
                        postingYear: originActgPeriod.strYear,
                        amount: -1 * totalBooking,
                        productRevenue: item.terminationDate ? item.terminatedProductRevenue : item.productRevenue,
                        extendedSalePrice: item.extendedSalePrice,
                        transactionType: config.originalTransactionType,
                        componentType: config.componentType,
                        isFutureTransaction: isFutureTransaction,
                        cummulativePercentRecognized: -1,
                        totalBooking: totalBooking,
                        processType: 'NORMAL',
                        productId: salesOrderItem.productId,
                        customerId: salesOrder.customerId,
                        salesOrderId: salesOrder.id,
                        isActive: item.isActive,
                        isModification: isMod
                    };

                    transactions.push(transaction);
                })
                needOriginalTransactions = false;
            }

            revenueArrangement.revenueArrangementItem.forEach(item => {
                let plan = config.getPlan(item);

                let { salesOrderItem } = item

                // let's say a closed state revenueArrangement is type mod for now
                let isMod = revenueArrangement.state == 'Closed'
                let newTransactions = plan.reduce(
                    (result, planElement) => {
                        // Scan period from current effective date to end date
                        // we are comparing endDate (function) which would give us un-expected behavior
                        let _endDate = Utils.formatDate(endDate)
                        if ((isMod & Period.isBefore(planElement.effectiveDate, _endDate))
                            || (!isMod && Period.isSameOrBefore(planElement.effectiveDate, _endDate))) {

                            let postingDate = planElement.actgPeriod.postingDate;
                            let postingPeriod = planElement.postingPeriod;
                            let postingYear = Period.toYear(planElement.effectiveDate);

                            if (Period.isSameOrBefore(planElement.effectiveDate, revenueArrangement.effectiveDate)) {
                                postingDate = arrangementFirstActgPeriod.postingDate
                                postingPeriod = arrangementFirstActgPeriod.period;
                                postingYear = arrangementFirstActgPeriod.strYear;
                            }

                            // Create plan transaction.
                            let t = {
                                id: shortid.generate(),
                                actgPeriod: planElement.actgPeriod,
                                planId: planElement.id,
                                revenueArrangementItemId: item.id,
                                salesOrderItemId: lastestVersionItem[item.salesOrderItem.rootId || item.salesOrderItemId],
                                salesOrderItemRootId: item.salesOrderItem.rootId || item.salesOrderItemId,
                                effectiveDate: planElement.effectiveDate,
                                postingDate: postingDate,
                                period: planElement.period,
                                postingPeriod: postingPeriod,
                                postingYear: postingYear,
                                amount: planElement.planAmount,
                                productRevenue: item.terminationDate ? item.terminatedProductRevenue : item.productRevenue,
                                extendedSalePrice: item.extendedSalePrice,
                                transactionType: config.ammortizationTransactionType,
                                componentType: config.componentType,
                                isFutureTransaction: Period.isSameOrAfter(postingPeriod, currentPostingPeriod),
                                cummulativePercentRecognized: planElement.cummulativePercentRecognized,
                                totalBooking: config.getTotalBooking(item),
                                processType: 'NORMAL',
                                productId: salesOrderItem.productId,
                                customerId: salesOrder.customerId,
                                salesOrderId: salesOrder.id,
                                jobId: salesOrder.jobId,
                                isActive: item.isActive,
                                isModification: isMod
                            }

                            result.push(t);

                            if (pComp(postingPeriod, firstPeriod) < 0)
                                firstPeriod = postingPeriod

                            if (pComp(postingPeriod, lastPeriod) > 0)
                                lastPeriod = postingPeriod
                        }

                        return result
                    }, []
                )
                transactions.push(...newTransactions);
            })
        }
    )

    salesOrder.transactions = transactions
    salesOrder.firstPeriod = firstPeriod;
    salesOrder.lastPeriod = lastPeriod;
}

export function generateExpenseWaterFall(salesOrder) {

    let config = CONFIG['expense'];

    let transactions = salesOrder['expenseTransactions'];

    let waterFall = {}

    salesOrder.revenueArrangement.forEach(ra => {
        ra.revenueArrangementItem.forEach(rai => {
            rai.expenseItems.forEach(expItem => {
                const itemId = expItem.salesOrderItemRootId || expItem.salesOrderItemId
                waterFall[itemId] = { salesOrderItem: expItem, salesOrderItemId: itemId }
            })
        })
    })

    let currentYear = Utils.getYear(Utils.currentDate());
    let currentPostingPeriod = Utils.formatShortestDateWithYear(Utils.currentDate());
    let totalPeriodWaterFall = { salesOrderItemId: 0 };
    waterFall[0] = totalPeriodWaterFall;

    transactions.forEach(
        transaction => {

            if (transaction.transactionType != config.ammortizationTransactionType)
                return;

            // Monthly water fall for current year only.
            let period = transaction.postingPeriod;
            let year = transaction.postingYear;

            let itemWaterFall = waterFall[transaction.salesOrderItemRootId];

            // By monthly period
            let periodAmount = itemWaterFall[period] || 0;
            periodAmount += transaction.amount || 0;
            itemWaterFall[period] = periodAmount;

            // By year
            let totalYearAmount = itemWaterFall[year] || 0;
            totalYearAmount += transaction.amount || 0;
            itemWaterFall[year] = totalYearAmount;

            // Year to date
            if ((!transaction.isFutureTransaction && year == currentYear) || (transaction.isFutureTransaction && period == currentPostingPeriod)) {
                let ytdAmount = itemWaterFall['ytd'] || 0;
                ytdAmount += transaction.amount || 0;
                itemWaterFall['ytd'] = ytdAmount;
            }

            // Full total
            let totalPeriodAmount = totalPeriodWaterFall[period] || 0;
            totalPeriodAmount += transaction.amount || 0;
            totalPeriodWaterFall[period] = totalPeriodAmount
        }
    )

    salesOrder['expenseWaterFall'] = waterFall
}

export function generateWaterFall(salesOrder) {

    let config = CONFIG['revenue'];

    let transactions = salesOrder.transactions

    let waterFall = salesOrder.salesOrderItem.reduce((result, salesOrderItem) => {
        let salesOrderItemId = salesOrderItem.rootId || salesOrderItem.id
        result[salesOrderItemId] = { salesOrderItem, salesOrderItemId }
        return result;
    }, {})

    let currentYear = Utils.getYear(Utils.currentDate());
    let currentPostingPeriod = Utils.formatShortestDateWithYear(Utils.currentDate());
    let totalPeriodWaterFall = { salesOrderItemId: 0 };
    waterFall[0] = totalPeriodWaterFall;

    transactions.forEach(
        transaction => {

            if (transaction.transactionType != config.ammortizationTransactionType)
                return;

            // Only adding waterfall for items which are active
            if (transaction.isActive) {
                // Monthly water fall for current year only.
                let period = transaction.postingPeriod;
                let year = transaction.postingYear;

                let itemWaterFall = waterFall[transaction.salesOrderItemRootId];

                // By monthly period
                let periodAmount = itemWaterFall[period] || 0;
                periodAmount += transaction.amount;
                itemWaterFall[period] = periodAmount;

                // By year
                let totalYearAmount = itemWaterFall[year] || 0;
                totalYearAmount += transaction.amount;
                itemWaterFall[year] = totalYearAmount;

                // Year to date
                if ((!transaction.isFutureTransaction && year == currentYear) || (transaction.isFutureTransaction && period == currentPostingPeriod)) {
                    let ytdAmount = itemWaterFall['ytd'] || 0;
                    ytdAmount += transaction.amount;
                    itemWaterFall['ytd'] = ytdAmount;
                }

                // Full total
                let totalPeriodAmount = totalPeriodWaterFall[period] || 0;
                totalPeriodAmount += transaction.amount;
                totalPeriodWaterFall[period] = totalPeriodAmount
            }
        }
    )

    salesOrder.waterFall = waterFall
}

export function generateSalesJournalReportData(salesOrder, journalAccounts, transactions, jeMappingColumns) {

    let config = CONFIG['revenue'];

    let journalEntries = [];

    let defaultAccounts = {}
    journalAccounts.forEach(account => {
        if (account.isDefault)
            defaultAccounts[account.accountType] = account
    })

    transactions.forEach(transaction => {
        if (!salesOrder.activeLinkId && transaction['Active Link Id'])
            salesOrder.activeLinkId = transaction['Active Link Id'];
        // Only report the unearned revenue component
        if (!(transaction.componentType === config.componentType)) {
            return;
        }

        journalEntries.push(...config.createEntries(transaction, salesOrder, transaction['Sales Order Item Id'], config, defaultAccounts, jeMappingColumns))

    });

    config.addJournalEntries(salesOrder, journalEntries)
}

export function generateExpenseJournalReportData(salesOrder, journalAccounts, transactions, jeMappingColumns) {
    let config = CONFIG['expense'];

    let journalEntries = [];

    let defaultAccounts = {}
    journalAccounts.forEach(account => {
        if (account.isDefault)
            defaultAccounts[account.accountType] = account
    })

    transactions.forEach(transaction => {
        if (!(transaction.componentType === config.componentType ||
            transaction['Component Type'] === config.componentType)) {
            return;
        }

        if (transaction.IsActive == undefined || transaction.IsActive == true) {
            journalEntries.push(...config.createEntries(transaction, salesOrder, transaction['Sales Order Item Id'], config, defaultAccounts, jeMappingColumns))
        }

    });

    config.addJournalEntries(salesOrder, journalEntries)
}

export function getTransactionsForExpenseReporting(salesOrder, calendarConfig) {
    let transactions = salesOrder.expenseTransactions;

    let expenseArrangementItemById = salesOrder.revenueArrangement.reduce((result, arrangement) => {
        arrangement.revenueArrangementItem.forEach(rai => {
            rai.expenseItems.forEach(item => {
                result[item.id] = item
            })
        });
        return result;
    }, {})

    let revenueArrangementItemById = salesOrder.revenueArrangement.reduce((result, arrangement) => {
        arrangement.revenueArrangementItem.forEach(item => result[item.id] = item);
        return result;
    }, {})

    transactions.sort(
        Comparator.getComparator(
            ["salesOrderExpenseItemId", "postingPeriod"],
            [Comparator.forType('string'), Comparator.forType('period')]
        )
    );

    let beginningCommissionPayable = 0
    let beginningCommissionExpense = 0
    let curSalesOrderItemId;
    let curSalesOrderItemExpenseId;
    let currReferenceNumber;
    let currClass;
    let currDepartment;
    let currLocation;
    let curSalesOrderItemRootId;
    let curPostingPeriod;
    let reportingTransaction;
    let reportingTransactions = []

    // ensure last transaction is posted
    transactions.push(endOfTransactions);
    transactions.forEach(transaction => {
        // Only report the unearned revenue componement
        if (!transaction.end && transaction.componentType != ComponentType.EXPENSE) {
            return;
        }

        if (curSalesOrderItemId != transaction.salesOrderItemId
            || curSalesOrderItemExpenseId != transaction.salesOrderExpenseItemId
            || currReferenceNumber != transaction.referenceNumber

            || currClass != transaction.class
            || currDepartment != transaction.department
            || currLocation != transaction.location
            || curPostingPeriod != transaction.postingPeriod) {

            if (reportingTransaction) {
                reportingTransaction["Ending Commission Payable"] = beginningCommissionPayable - reportingTransaction["Actual Commission this month"];
                reportingTransaction["Commission Amount"] = reportingTransaction["Ending Commission Payable"] + reportingTransaction["Commission Expense"] - reportingTransaction["Beginning Commission Payable"]
                reportingTransactions.push(reportingTransaction);

                if (curSalesOrderItemRootId == transaction.salesOrderItemRootId && curSalesOrderItemExpenseId == transaction.salesOrderExpenseItemId) {
                    // next month's beginning is current months ending reveune..
                    beginningCommissionExpense += reportingTransaction["Commission Expense"];
                    beginningCommissionPayable = reportingTransaction["Ending Commission Payable"];
                } else {
                    beginningCommissionExpense = 0
                    beginningCommissionPayable = 0
                }
            }

            curSalesOrderItemId = transaction.salesOrderItemId;
            curSalesOrderItemRootId = transaction.salesOrderItemRootId;
            curSalesOrderItemExpenseId = transaction.salesOrderExpenseItemId
            currReferenceNumber = transaction.referenceNumber

            currClass = transaction.class
            currDepartment = transaction.department
            currLocation = transaction.location
            curPostingPeriod = transaction.postingPeriod;

            reportingTransaction = undefined;
        }

        // The end
        if (transaction.end) return;

        if (!reportingTransaction) {

            let {
                postingPeriod,
                revenueArrangementItemId,
                expenseArrangementItemId,
            } = transaction

            let revenueArrangementItem = revenueArrangementItemById[revenueArrangementItemId];
            const expenseArrangementItem = expenseArrangementItemById[expenseArrangementItemId]

            let { salesOrderItem } = revenueArrangementItem;

            let expenseActgPeriod = Utils.formatShortestDateWithYear(expenseArrangementItem.expenseDate);

            reportingTransaction = {
                "Actg Period": postingPeriod,
                "Quarter": Utils.getQuarter(postingPeriod),
                "Year": Utils.getYearFromPeriod(postingPeriod),
                "Customer Id": salesOrder.customerId,
                "Sales Order Id": salesOrder.id,
                "Sales Order Item Id": transaction.salesOrderItemRootId,
                "Sales Order Item Root Id": transaction.salesOrderItemRootId,
                "Product Id": salesOrderItem.product.id,
                "Beginning Commission Expense": Utils.parseAmount(beginningCommissionExpense),
                "Beginning Commission Payable": Utils.parseAmount(beginningCommissionPayable),
                // Commission Amortization
                "Commission Expense": 0,
                "Actual Commission this month": 0,
                // New Deferral
                "Commission Amount": (expenseActgPeriod == postingPeriod
                    && expenseArrangementItem.allocatedExpense && Utils.parseAmount(expenseArrangementItem.allocatedExpense)
                ) || 0,
                "Job Id": salesOrder.jobId,
                "IsActive": revenueArrangementItem.isActive,
                "Class": expenseArrangementItem.class,
                "Department": expenseArrangementItem.department,
                "Location": expenseArrangementItem.location,
                "Expense Type": expenseArrangementItem.expenseType,
                'Expense Code': expenseArrangementItem.expenseCode,
                'Reference Number': expenseArrangementItem.referenceNumber,
                'Sales Order Expense Item Id': expenseArrangementItem.salesOrderExpenseItemId,
                'Sales Person': expenseArrangementItem.salesPerson?.name
            }
        }

        reportingTransaction["Actual Commission this month"] += transaction.amount;

        if (transaction.transactionType == TransactionType.EXP_RECOGNIZED)
            reportingTransaction["Commission Expense"] += transaction.amount;
    });
    transactions.pop();
    salesOrder.expenseReportingTransactions = reportingTransactions;
}

export function getTransactionsForReporting(salesOrder, orgConfigByKey, calendarConfig) {

    const enableAdjustmentToUnearnedRevenue = (
        orgConfigByKey["properties/enableAdjustmentToUnearnedRevenue"]
        && orgConfigByKey["properties/enableAdjustmentToUnearnedRevenue"].value
    ) || false

    const billingByContract = (
        orgConfigByKey['properties/billing/by-contract'] && orgConfigByKey['properties/billing/by-contract'].value
    ) || false

    const { transactions } = salesOrder;

    let revenueArrangementItemById = salesOrder.revenueArrangement.reduce((result, arrangement) => {
        arrangement.revenueArrangementItem.forEach(item => result[item.id] = item);
        return result;
    }, {})

    // Maintains a map of rev arrangement that was created when one arrangement was closed.
    let nextRevenueArrangementItemById = salesOrder.revenueArrangement.reduce((result, arrangement, index, allArrangements) => {
        if (index < allArrangements.length - 1) {
            let nextArrangement = allArrangements[index + 1]
            arrangement.revenueArrangementItem.forEach(item => {
                result[item.id] = nextArrangement.revenueArrangementItem.find(nextItem => (item.salesOrderItem.rootId || item.salesOrderItemId) === (nextItem.salesOrderItem.rootId || nextItem.salesOrderItemId))
            });
        }

        return result;
    }, {})

    transactions.sort(
        Comparator.getComparator(
            ["salesOrderItemId", "postingPeriod"],
            [Comparator.forType('string'), Comparator.forType('period')]
        )
    );

    let beginningBalance = 0
    let curSalesOrderItemId;
    let curSalesOrderItemRootId;

    let reportingTransaction;
    let reportingTransactions = []

    const billingTransactions = []


    // forces last transaction to be posted in loop
    transactions.push(endOfTransactions);

    let scheduleBySalesOrderItemId = prepareScheduleBySalesOrderItemId(salesOrder.billingSchedule);

    let adjustmentPeriods = []
    const firstActgPeriod = Period.toActgPeriod(salesOrder.orderDate, calendarConfig);
    const salesOrderFirstActgPeriod = firstActgPeriod.period;

    salesOrder.revenueArrangement.forEach((revenueArrangement, index) => {
        let arrangementEndDate = revenueArrangement.effectiveDate
        const effectiveActgPeriod = Period.toActgPeriod(revenueArrangement.effectiveDate, calendarConfig)
        let effectivePeriod = effectiveActgPeriod.period;

        // In case of modification, we dont want to use earlier arrangements to determine the adjustment period.
        // prior revenue arrangement is only used in cases where contract was
        // modified after the termination date (deliveryEndDate of the revenueArrangementItem with longest duration in
        // the prior arrangement).

        if (adjustmentPeriods[adjustmentPeriods.length - 1] > effectivePeriod) {
            adjustmentPeriods.pop();
        }

        // Set end date to the deliveryEndDate of the revenueArrangementItem with longest duration
        revenueArrangement.revenueArrangementItem.forEach(item => {
            let itemEndDate = item.deliveryEndDate
            if (!itemEndDate)
                return;

            if (!arrangementEndDate || (arrangementEndDate && Period.isAfter(itemEndDate, arrangementEndDate))) {
                arrangementEndDate = itemEndDate
            }
        })
        const adjustmentActgPeriod =
            Period.toActgPeriod(arrangementEndDate, calendarConfig)
        adjustmentPeriods.push(adjustmentActgPeriod.period);
    })

    adjustmentPeriods = [...new Set(adjustmentPeriods)].sort()

    let lastPeriodDate = Period.toActgPeriod(adjustmentPeriods[adjustmentPeriods.length - 1], calendarConfig).startDate

    salesOrder.currentRevenueArrangement.revenueArrangementItem.forEach(item => {
        let itemEndDate = item.deliveryEndDate
        let itemLatestBillingDate = itemEndDate

        let itemSchedule = scheduleBySalesOrderItemId[item.salesOrderItem.rootId || item.salesOrderItemId]

        if (itemSchedule && Array.isArray(itemSchedule.schedule) && itemSchedule.schedule.length > 0) {
            itemSchedule.schedule.forEach(schedule => {
                if (!itemLatestBillingDate || (itemLatestBillingDate && schedule.billingDate && Period.isAfter(schedule.billingDate, itemLatestBillingDate))) {
                    itemLatestBillingDate = schedule.billingDate
                }
            })
        }
        //use the latest invoice as the item's latest billing date

        if (!lastPeriodDate ||
            (itemLatestBillingDate && lastPeriodDate && Period.isAfter(itemLatestBillingDate, lastPeriodDate))) {
            lastPeriodDate = itemLatestBillingDate
        }
    })

    // salesOrderLastActgPeriod

    let lastActgPeriod = Period.toActgPeriod(lastPeriodDate, calendarConfig);
    let lastPeriod = lastActgPeriod.period

    // Reversal Transactions Set
    const reversalTransactions = transactions.filter(transaction => {
        if (!transaction.end && transaction.transactionType === 'REVENUE' && transaction.processType === 'REVERSAL') return transaction
    })

    reversalTransactions.sort(Comparator.getComparator(
        ["salesOrderItemId", "postingPeriod"],
        [Comparator.forType('string'), Comparator.forType('period')]
    ))

    const reversalTransactionsByItemId = Utils.groupByOnArray(reversalTransactions, T => T.salesOrderItemId)

    let currActgPeriod;
    transactions.forEach(transaction => {
        // Only report the unearned revenue component
        if (!transaction.end && transaction.componentType != ComponentType.UNEARNED_REVENUE) {
            return;
        }

        if (curSalesOrderItemId != transaction.salesOrderItemId
            || currActgPeriod && (currActgPeriod.period != transaction.postingPeriod)) {

            let salesOrderLastActgPeriod = adjustmentPeriods[0]
            if (currActgPeriod && (currActgPeriod.period > salesOrderLastActgPeriod) && adjustmentPeriods.length > 1) {
                adjustmentPeriods.shift()
                salesOrderLastActgPeriod = adjustmentPeriods[0]
            }

            if (reportingTransaction) {

                reportingTransaction["Ending Backlog"] = beginningBalance + reportingTransaction["New Booking"] - reportingTransaction["Revenue"];

                if (curSalesOrderItemRootId == transaction.salesOrderItemRootId) {
                    beginningBalance = reportingTransaction["Ending Backlog"];
                } else {
                    beginningBalance = 0
                }

                // ******* Adjust for Billing  ****** //

                let itemBillingSchedule = scheduleBySalesOrderItemId[curSalesOrderItemRootId]
                if (itemBillingSchedule) {
                    const _reportingTransactions = adjustForBilling(itemBillingSchedule, reportingTransaction,
                        enableAdjustmentToUnearnedRevenue && currActgPeriod.period === salesOrderLastActgPeriod,
                        currActgPeriod, salesOrderFirstActgPeriod);
                    reportingTransaction = Utils.cloneDeep(_reportingTransactions.revenue[0])

                    if (_reportingTransactions.billing.length > 0) {
                        billingTransactions.push(..._reportingTransactions.billing)
                        const aggBillingTransactions = Utils.agg(_reportingTransactions.billing, ['Actg Period'], ['Actg Period'], [], [], ['New Billing'])
                        reportingTransaction['New Billing'] = aggBillingTransactions[0]['New Billing']
                    }
                    reportingTransactions.push(_reportingTransactions.revenue[0]);

                } else {
                    reportingTransactions.push(reportingTransaction);
                }

                // salesOrder item has changed.
                if (curSalesOrderItemRootId != transaction.salesOrderItemRootId) {
                    if (currActgPeriod.period < salesOrderLastActgPeriod) {
                        //todo @irfan need to understand what this is?
                        const rollTill = rollUnearnedRevenueTillLastPeriod(currActgPeriod, salesOrderLastActgPeriod,
                            reportingTransaction, itemBillingSchedule, false,
                            enableAdjustmentToUnearnedRevenue,
                            salesOrderFirstActgPeriod)
                        rollTill.forEach(RT => {
                            billingTransactions.push(...RT.billing)
                            reportingTransactions.push(...RT.revenue);
                        })
                    }

                    let lastReportingTransaction = reportingTransactions[reportingTransactions.length - 1]
                    if (lastReportingTransaction["Actg Period"] < lastPeriod) {
                        const lastTransactionActgPeriod =
                            currActgPeriod.get(lastReportingTransaction["Actg Period"])
                        const rollTill = rollUnearnedRevenueTillLastPeriod(lastTransactionActgPeriod,
                            lastPeriod, lastReportingTransaction,
                            itemBillingSchedule, true,
                            enableAdjustmentToUnearnedRevenue,
                            salesOrderFirstActgPeriod)
                        rollTill.forEach(RT => {
                            billingTransactions.push(...RT.billing)
                            reportingTransactions.push(...RT.revenue);
                        })
                    }

                } else if (transaction.isModification) {
                    // If there is any gap between current and last transaction period
                    // fill that gap from last till current - 1 period
                    let lastReversalTransaction = reversalTransactionsByItemId[transaction.salesOrderItemId]
                    if (lastReversalTransaction && lastReversalTransaction.length) {
                        lastReversalTransaction = lastReversalTransaction[lastReversalTransaction.length -
                            1]
                        const nextTransactionPeriod = lastReversalTransaction.actgPeriod.nextPeriod
                        const currentPostingPeriod = transaction.actgPeriod.period

                        if (!Period.isSame(nextTransactionPeriod,
                            currentPostingPeriod) &&
                            !Period.isBefore(currentPostingPeriod,
                                nextTransactionPeriod)) {
                            // Add missing reportingTransactions till postingPeriod
                            const _reportingTransaction = Utils.copy(
                                reportingTransaction)

                            const missingTransactions = getMissingReportingTransactions(
                                lastReversalTransaction.get(
                                    nextTransactionPeriod),
                                transaction.actgPeriod, _reportingTransaction)

                            reportingTransactions.push(...missingTransactions)
                        }
                    }

                }
            }

            curSalesOrderItemId = transaction.salesOrderItemId;
            curSalesOrderItemRootId = transaction.salesOrderItemRootId;
            currActgPeriod = transaction.actgPeriod;

            reportingTransaction = undefined;
        }

        if (transaction.end) return;

        if (!reportingTransaction) {

            let {
                postingPeriod,
                revenueArrangementItemId,
                salesOrderItemId,
                salesOrderItemRootId,
                isModification,
                effectiveDate,
                componentType,
                actgPeriod
            } = transaction

            let revenueArrangementItem = revenueArrangementItemById[revenueArrangementItemId];
            const raiStartActgPeriod = Period.toActgPeriod(revenueArrangementItem.effectiveDate, calendarConfig);
            const raiEndDate = revenueArrangementItem.endDate || Period.currentDate();
            const raiEndActgPeriod = Period.toActgPeriod(raiEndDate, calendarConfig);

            const raiAccountingPeriod = raiStartActgPeriod.period;
            const raiEndPeriod = raiEndActgPeriod.period

            const { salesOrderItem } = revenueArrangementItem;

            reportingTransaction = {
                "Actg Period": actgPeriod.period,
                "Quarter": actgPeriod.strQuarter,
                "Year": actgPeriod.strYear,
                "Sales Order Id": salesOrder.id,
                "Sales Order Item Id": salesOrderItemId,
                "Sales Order Item Root Id": salesOrderItemRootId,
                "Customer Id": salesOrder.customerId,
                "Product Id": salesOrderItem.product.id,
                "Beginning Balance": Utils.parseAmount(beginningBalance),
                'Revenue': 0,
                "Job Id": salesOrder.jobId,
                "IsActive": revenueArrangementItem.isActive,
                "isModification": isModification,
                "Adjustment To Unearned Revenue": 0,
                "Transaction Date": effectiveDate,
                "Component Type": componentType,
                "revenueArrangementItemId": revenueArrangementItemId,
                "Reference Invoice Number": billingByContract && salesOrderItem.referenceInvoiceNumber || 'None',
                "Subscription ID": billingByContract && salesOrderItem.subscriptionId || 'None',
                "Invoice Number": billingByContract && salesOrderItem.invoiceNumber || 'None'
            }

            if (isModification == true && Period.isSame(postingPeriod, raiEndPeriod)) {
                let nextRevenueArranagementItem = nextRevenueArrangementItemById[revenueArrangementItemId];

                let closedProductRevenue = Utils.parseAmount(
                    revenueArrangementItem.terminationDate 
                        ? revenueArrangementItem.terminatedProductRevenue
                        : revenueArrangementItem.productRevenue
                );
                let newProductRevenue = Utils.parseAmount(
                    nextRevenueArranagementItem.terminationDate 
                        ? nextRevenueArranagementItem.terminatedProductRevenue
                        : nextRevenueArranagementItem.productRevenue
                );
                reportingTransaction["New Booking"] = (raiEndPeriod == postingPeriod && closedProductRevenue != newProductRevenue) && (newProductRevenue - closedProductRevenue) || 0

                let closedSalePrice = Utils.parseAmount(revenueArrangementItem.extendedSalePrice);
                let newSalePrice = Utils.parseAmount(nextRevenueArranagementItem.extendedSalePrice);
                reportingTransaction["New Unbilled"] = (raiEndPeriod == postingPeriod && closedSalePrice != newSalePrice) && (newSalePrice - closedSalePrice) || 0
            } else {
                if (revenueArrangementItem.terminationDate) {
                    reportingTransaction["New Booking"] = (
                        raiAccountingPeriod == postingPeriod 
                            && revenueArrangementItem.terminatedProductRevenue 
                            && Utils.parseAmount(revenueArrangementItem.terminatedProductRevenue)
                        ) || 0
                } else {
                    reportingTransaction["New Booking"] = (
                        raiAccountingPeriod == postingPeriod 
                            && revenueArrangementItem.productRevenue 
                            && Utils.parseAmount(revenueArrangementItem.productRevenue)
                        ) || 0
                }
                reportingTransaction["New Unbilled"] = (raiAccountingPeriod == postingPeriod && revenueArrangementItem.extendedSalePrice && Utils.parseAmount(revenueArrangementItem.extendedSalePrice)) || 0
            }
        }

        if (transaction.transactionType == TransactionType.REVENUE)
            reportingTransaction['Revenue'] += transaction.amount;
    });
    // remove dummy last transaction.
    transactions.pop();

    let period = 'Actg Period';

    let orderRevenueDeferralByPeriod = Utils.agg(
        reportingTransactions, [period], [period], [], [], ['Beginning Net Deferral', 'Net Deferral'],
        {
            'Net Deferral': item => item["Ending Unearned Revenue"] - item["Adjustment To Unearned Revenue"],
            'Beginning Net Deferral': item => item["Beginning Unearned Revenue"]
        }
    )

    orderRevenueDeferralByPeriod = Utils.arrayToObject(
        orderRevenueDeferralByPeriod,
        item => item[period],
        item => ({
            'Net Deferral': item['Net Deferral'],
            'Beginning Net Deferral': item['Beginning Net Deferral'],
        })
    )

    reportingTransactions.forEach(T => {
        let beginningNetDeferral = orderRevenueDeferralByPeriod[T[period]]['Beginning Net Deferral'];
        let hadBeginningDeferral = (beginningNetDeferral > 0 || Utils.isNumberSame(beginningNetDeferral, 0))
        T['Beginning Net Deferral'] = beginningNetDeferral
        T['Beginning Deferred Revenue'] = hadBeginningDeferral ? T['Beginning Unearned Revenue'] : 0
        T['Beginning Unbilled Receivable'] = hadBeginningDeferral ? 0 : -1 * T['Beginning Unearned Revenue']

        let netDeferral = orderRevenueDeferralByPeriod[T[period]]['Net Deferral'];
        let hasDeferral = (netDeferral > 0 || Utils.isNumberSame(netDeferral, 0))
        T[`Net Deferral`] = netDeferral
        T["Ending Deferred Revenue"] = hasDeferral ? T["Ending Unearned Revenue"] : 0
        T["Ending Unbilled Receivable"] = hasDeferral ? 0 : -1 * T["Ending Unearned Revenue"]

        T['isBillingRow'] = false
    });

    billingTransactions.forEach(T => T = Object.assign(T, {
        'Beginning Net Deferral': 0,
        'Beginning Deferred Revenue': 0,
        'Beginning Unbilled Receivable': 0,
        'Net Deferral': 0,
        'Ending Deferred Revenue': 0,
        'Ending Unbilled Receivable': 0,
        'isBillingRow': true
    }))

    let transactionsToReturn = [...reportingTransactions, ...billingTransactions]

    if (enableAdjustmentToUnearnedRevenue) {
        const finalTransactionItems = reportingTransactions.filter(
            T => T['Actg Period'] === lastPeriod);
        for (let T of finalTransactionItems) {
            if (T['Ending Unearned Revenue'] && T['Ending Unearned Revenue'] !==
                0.0) {
                T['Adjustment To Unearned Revenue'] = -1 *
                    T['Ending Unearned Revenue']
                T['Ending Unearned Revenue'] = 0.0
            }
        }
    }
    salesOrder.reportingTransactions = transactionsToReturn
}

// This function rolls unearned for each product item till the last period of the sales order.
// This ensure snapshot at the customer level in each period has the correct total unearned number.
function rollUnearnedRevenueTillLastPeriod(currActgPeriod, lastPeriod, reportingTransaction,
    itemBillingSchedule, ensureZeroUnearnedRevenue = false,
    enableAdjustmentToUnearnedRevenue = false,
    salesOrderFirstActgPeriod) {

    if (Period.isSameOrAfter(currActgPeriod.period, lastPeriod)) return [];

    // add empty transaction till last period for current sales order.
    const missingPeriods = Period.periodsBetween(currActgPeriod.period, lastPeriod, false, true);
    let actgPeriod = currActgPeriod;
    return missingPeriods.map(period => {
        actgPeriod = actgPeriod.get(period);

        let currentReportingTransaction = Object.assign({}, reportingTransaction, {
            "Actg Period": actgPeriod.period,
            "Transaction Date": actgPeriod.effectiveDate,
            "Quarter": actgPeriod.strQuarter,
            "Year": actgPeriod.strYear,
            "Beginning Unearned Revenue": reportingTransaction["Ending Unearned Revenue"],
            "Ending Unearned Revenue": reportingTransaction["Ending Unearned Revenue"],
            "Beginning Unbilled": reportingTransaction["Ending Unbilled"],
            "Ending Unbilled": reportingTransaction["Ending Unbilled"],
            "Beginning Balance": 0,
            'Revenue': 0,
            "New Booking": 0,
            "New Unbilled": 0,
            "New Billing": 0
        })

        if (itemBillingSchedule) {
            currentReportingTransaction = adjustForBilling(itemBillingSchedule,
                currentReportingTransaction,
                enableAdjustmentToUnearnedRevenue &&
                (period === lastPeriod || ensureZeroUnearnedRevenue),
                actgPeriod,
                salesOrderFirstActgPeriod)
        }

        return currentReportingTransaction;
    })
}

function getMissingReportingTransactions(fromActgPeriod, toActgPeriod, lastReportingTransaction) {
    // add empty transaction till last period for current sales order.
    const missingPeriods = Utils.periodsBetween(fromActgPeriod.period, toActgPeriod.period, true);

    const missingReportingTransactions = []

    let actgPeriod = fromActgPeriod
    missingPeriods.forEach(period => {
        actgPeriod = actgPeriod.get(period)

        let transaction = Object.assign({}, lastReportingTransaction, {
            "Actg Period": actgPeriod.period,
            'Transaction Date': actgPeriod.endDate,
            "Quarter": actgPeriod.strQuarter,
            "Year": actgPeriod.strYear,
            "Beginning Unearned Revenue": lastReportingTransaction["Ending Unearned Revenue"],
            "Booking Amount": 0,
            "Revenue": 0,
            "Ending Unearned Revenue": lastReportingTransaction["Ending Unearned Revenue"],
            "Beginning Unbilled": 0,
            "New Billing": 0,
            "Ending Unbilled": 0,
            "Beginning Balance": 0,
            "New Booking": 0,
            "New Unbilled": 0,
            "Ending Backlog": 0,
            "Adjustment To Unearned Revenue": 0
        })

        missingReportingTransactions.push(transaction)
    })

    return missingReportingTransactions
}
